import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type {
  AgreementRateTrendPoint,
  ExceptionsByTier,
  OverrideDirectionPoint,
  OverrideRatePoint,
  ReasonCodeCount,
  ReasonCodeTrendSeries,
} from "../lib/api/types";

const AXIS = { fill: "hsl(var(--muted-foreground))", fontSize: 11 };
const TOOLTIP = {
  background: "hsl(var(--popover))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "0px",
  color: "hsl(var(--popover-foreground))",
  fontSize: 12,
};

const PG_COLOR = "hsl(var(--chart-1))";
const CORP_COLOR = "hsl(var(--chart-2))";

/** Non-judgmental pair for override direction — neither band is "the bad one". */
const DIRECTION_COLORS = {
  approveOverDecline: "hsl(var(--chart-1))",
  declineOverApprove: "hsl(var(--chart-5))",
};

/** Colors for the top-3 reason codes, shared between bar chart and trend lines. */
export const REASON_COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-5))"];
const REASON_REST = "var(--bar-muted)";

export function OverrideRateTrendChart({ series }: { series: OverrideRatePoint[] }) {
  return (
    <div className="h-[300px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={series} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="period" tick={AXIS} stroke="hsl(var(--border))" />
          <YAxis
            tick={AXIS}
            stroke="hsl(var(--border))"
            width={44}
            tickFormatter={(v: number) => `${Math.round(v)}%`}
          />
          <Tooltip
            contentStyle={TOOLTIP}
            formatter={(v: number, name: string) => [`${v}%`, name]}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Line
            type="monotone"
            dataKey="overrideRatePg"
            name="PG override rate"
            stroke={PG_COLOR}
            strokeWidth={2}
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="overrideRateCorp"
            name="Corp override rate"
            stroke={CORP_COLOR}
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function OverrideDirectionChart({ series }: { series: OverrideDirectionPoint[] }) {
  return (
    <div className="h-[300px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={series} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="period" tick={AXIS} stroke="hsl(var(--border))" />
          <YAxis tick={AXIS} stroke="hsl(var(--border))" width={40} />
          <Tooltip contentStyle={TOOLTIP} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar
            dataKey="approveOverDecline"
            name="Approved what Athena declined"
            stackId="dir"
            fill={DIRECTION_COLORS.approveOverDecline}
          />
          <Bar
            dataKey="declineOverApprove"
            name="Declined what Athena approved"
            stackId="dir"
            fill={DIRECTION_COLORS.declineOverApprove}
            radius={[3, 3, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ReasonCodeBarChart({ data }: { data: ReasonCodeCount[] }) {
  return (
    <div className="h-[300px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 8, right: 16, bottom: 4, left: 8 }}
        >
          <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" tick={AXIS} stroke="hsl(var(--border))" />
          <YAxis
            type="category"
            dataKey="reasonCode"
            tick={{ ...AXIS, fontSize: 10 }}
            stroke="hsl(var(--border))"
            width={132}
          />
          <Tooltip contentStyle={TOOLTIP} formatter={(v: number) => [`${v} overrides`, "Count"]} />
          <Bar dataKey="count" name="Overrides" radius={[0, 3, 3, 0]}>
            {data.map((entry, i) => (
              <Cell key={entry.reasonCode} fill={i < 3 ? REASON_COLORS[i] : REASON_REST} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ReasonCodeTrendChart({ series }: { series: ReasonCodeTrendSeries[] }) {
  const periods = series[0]?.trend.map((p) => p.period) ?? [];
  const data = periods.map((period, i) => {
    const row: Record<string, string | number> = { period };
    for (const s of series) row[s.reasonCode] = s.trend[i]?.count ?? 0;
    return row;
  });

  return (
    <div className="h-[300px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="period" tick={AXIS} stroke="hsl(var(--border))" />
          <YAxis tick={AXIS} stroke="hsl(var(--border))" width={40} />
          <Tooltip contentStyle={TOOLTIP} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {series.map((s, i) => (
            <Line
              key={s.reasonCode}
              type="monotone"
              dataKey={s.reasonCode}
              name={s.reasonCode}
              stroke={REASON_COLORS[i]}
              strokeWidth={2}
              dot={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ExceptionsByTierChart({ data }: { data: ExceptionsByTier[] }) {
  return (
    <div className="h-[300px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="tier" tick={AXIS} stroke="hsl(var(--border))" />
          <YAxis tick={AXIS} stroke="hsl(var(--border))" width={40} />
          <Tooltip contentStyle={TOOLTIP} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar
            dataKey="candidateCount"
            name="Candidate (unreconciled)"
            stackId="tier"
            fill="var(--neutral-inactive)"
            maxBarSize={72}
          />
          <Bar
            dataKey="confirmedCount"
            name="Confirmed"
            stackId="tier"
            fill="var(--amber)"
            radius={[3, 3, 0, 0]}
            maxBarSize={72}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function AgreementRateTrendChart({ series }: { series: AgreementRateTrendPoint[] }) {
  return (
    <div className="h-[300px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={series} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="period" tick={AXIS} stroke="hsl(var(--border))" />
          <YAxis
            tick={AXIS}
            stroke="hsl(var(--border))"
            width={44}
            domain={[60, 100]}
            tickFormatter={(v: number) => `${Math.round(v)}%`}
          />
          <Tooltip contentStyle={TOOLTIP} formatter={(v: number, name: string) => [`${v}%`, name]} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Line
            type="monotone"
            dataKey="agreementRatePg"
            name="PG agreement rate"
            stroke={PG_COLOR}
            strokeWidth={2}
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="agreementRateCorp"
            name="Corp agreement rate"
            stroke={CORP_COLOR}
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
